import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { workspaceAPI } from 'features/workspace/api/workspace';
import { difference, groupBy, orderBy } from 'lodash';
import {
  deleteCustomViewAPI,
  duplicateCustomViewAPI,
  fetchProjectCustomViewsAPI,
  saveProjectCustomViewAPI,
  updateCustomViewAPI,
} from '../../../services/CustomViewService';
import {
  CreateProjectAPI,
  DeleteProjectAPI,
  DuplicateProjectAPI,
  FetchProjectsAPI,
  GetProjectDetailsAPI,
  UpdateProjectAPI,
  addPermissionsAPI,
  deletePermissionsAPI,
  fetchProjectUserPermissionsAPI,
  fetchProjectUsersAPI,
  updatePermissionsAPI,
  updateProjectKpiAPI,
} from '../api/ProjectService';

/**
 * Create a new project
 * @param {Object} projectData - project object
 * @param {String} projectData.name - name of the project
 * @param {String} projectData.description - description of the project
 * @param {String} projectData.budget - budget of the project
 * @param {Number} projectData.labor_percentage - labor percentage of the project
 * @param {Number} projectData.labor_rate - budget of project
 * @param {String} projectData.start_date - start date of the project
 * @param {String} projectData.sub_completion_date - target completion date of the project
 * @param {Number} projectData.shift - shift of the project
 * @param {Boolean} projectData.monday - monday of the project
 * @param {Boolean} projectData.tuesday - tuesday of the project
 * @param {Boolean} projectData.wednesday - wednesday of the project
 * @param {Boolean} projectData.thursday - thursday of the project
 * @param {Boolean} projectData.friday - friday of the project
 * @param {Boolean} projectData.saturday - saturday of the project
 * @param {Boolean} projectData.sunday - sunday of the project
 */
export const saveProject = createAsyncThunk(
  'projects/saveProject',
  async (projectData, { rejectWithValue }) => {
    const apiResponse = await CreateProjectAPI(projectData);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Fetch the list of all projects from api
 */
export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (args = {}, { rejectWithValue }) => {
    try {
      const apiResponse = await FetchProjectsAPI();
      if (apiResponse.error) {
        return rejectWithValue(apiResponse.error);
      } else if (Array.isArray(apiResponse.response.data)) {
        return apiResponse.response.data.map((project) => {
          project['loading'] = false;
          return project;
        });
      } else {
        return apiResponse.response.data;
      }
    } catch (error) {
      console.log('Error =>', error);
    }
  }
);

/**
 * Fetch project details from API
 */
export const fetchProject = createAsyncThunk(
  'projects/fetchProject',
  async (projectId, { rejectWithValue }) => {
    const apiResponse = await GetProjectDetailsAPI({ id: projectId });
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      apiResponse.response.data['loading'] = false;
      return apiResponse.response.data;
    }
  }
);

/**
 * Update project details
 * @param {Object} projectData - project object
 * @param {Number} projectData.id - id of the project
 * @param {String} projectData.name - name of the project
 * @param {String} projectData.description - description of the project
 * @param {String} projectData.budget - budget of the project
 * @param {Number} projectData.labor_percentage - labor percentage of the project
 * @param {Number} projectData.labor_rate - budget of project
 * @param {String} projectData.start_date - start date of the project
 * @param {String} projectData.sub_completion_date - target completion date of the project
 * @param {Number} projectData.shift - shift of the project
 * @param {Boolean} projectData.monday - monday of the project
 * @param {Boolean} projectData.tuesday - tuesday of the project
 * @param {Boolean} projectData.wednesday - wednesday of the project
 * @param {Boolean} projectData.thursday - thursday of the project
 * @param {Boolean} projectData.friday - friday of the project
 * @param {Boolean} projectData.saturday - saturday of the project
 * @param {Boolean} projectData.sunday - sunday of the project
 */
export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async (projectData, { rejectWithValue }) => {
    const apiResponse = await UpdateProjectAPI(projectData);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      apiResponse.response.data['loading'] = false;
      return apiResponse.response.data;
    }
  }
);

/**
 * Delete project from API
 */
export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (projectId, { rejectWithValue }) => {
    const apiResponse = await DeleteProjectAPI({ id: projectId });
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Duplicate project from API
 */
export const duplicateProject = createAsyncThunk(
  'projects/duplicateProject',
  async (projectId, { rejectWithValue }) => {
    const apiResponse = await DuplicateProjectAPI({ id: projectId });
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Add project permissions
 * @param {Integer} projectId - project id
 * @param {[Object]} permissions - permissions object
 * @param {Integer} permissions[].id - user id
 * @param {String} permissions[].access_level - user's id
 * @param {String} permissions[].project_role - user's project role
 */
export const addProjectPermissions = createAsyncThunk(
  'projects/addProjectPermissions',
  async ({ projectId, permissions }, { rejectWithValue }) => {
    const apiResponse = await addPermissionsAPI({ projectId, permissions });
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Update project permissions
 * @param {Integer} projectId - project id
 * @param {[Object]} permissions - permissions object
 * @param {Integer} permissions[].id - user id
 * @param {String} permissions[].access_level - user's id
 * @param {String} permissions[].project_role - user's project role
 */
export const updateProjectPermissions = createAsyncThunk(
  'projects/updateProjectPermissions',
  async ({ projectId, permissions }, { rejectWithValue }) => {
    const apiResponse = await updatePermissionsAPI({ projectId, permissions });
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Delete project permissions
 * @param {Integer} projectId - project id
 * @param {[integer]} userIds - array of user ids to remove
 */
export const deleteProjectPermissions = createAsyncThunk(
  'projects/deleteProjectPermissions',
  async ({ projectId, userIds }, { rejectWithValue }) => {
    const apiResponse = await deletePermissionsAPI({ projectId, userIds });
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Get Users with access to project
 * @param {Number} projectId - project id
 */
export const fetchProjectUsers = createAsyncThunk(
  'projects/fetchProjectUsers',
  async (projectId, { rejectWithValue }) => {
    const apiResponse = await fetchProjectUsersAPI(projectId);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/** Get project user permissions */
export const fetchProjectUserPermissions = createAsyncThunk(
  'projects/fetchProjectUserPermissions',
  async (projectId, { rejectWithValue }) => {
    const apiResponse = await fetchProjectUserPermissionsAPI(projectId);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Get project report
 * @param {number} projectId - project id
 */
export const fetchProjectCustomViews = createAsyncThunk(
  'projects/fetchProjectCustomViews',
  async (_, { rejectWithValue }) => {
    const apiResponse = await fetchProjectCustomViewsAPI();
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response;
    }
  }
);

/**
 * Save project custom view
 * @param {object} data
 * @param {object} data.parameters
 */
export const saveProjectCustomView = createAsyncThunk(
  'projects/saveProjectCustomView',
  async (data, { rejectWithValue }) => {
    const apiResponse = data.id
      ? await updateCustomViewAPI(data)
      : await saveProjectCustomViewAPI(data);
    // const apiResponse = await saveProjectCustomViewAPI(data);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response;
    }
  }
);

/**
 * Duplicate  custom views
 * @param {number} id - customView id
 */
export const duplicateProjectCustomView = createAsyncThunk(
  'projects/duplicateProjectCustomView',
  async (id, { rejectWithValue }) => {
    const apiResponse = await duplicateCustomViewAPI(id);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response;
    }
  }
);

/**
 * Delete custom views
 * @param {number} id - customView id
 */
export const deleteProjectCustomView = createAsyncThunk(
  'projects/deleteProjectCustomView',
  async (id, { rejectWithValue }) => {
    const apiResponse = await deleteCustomViewAPI(id);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response;
    }
  }
);

/**
 * Update project KPI
 * @param {number} id - projectId
 */
export const updateProjectKpi = createAsyncThunk(
  'projects/updateProjectKpi',
  async (id, { rejectWithValue }) => {
    const apiResponse = await updateProjectKpiAPI(id);
    console.log('API Response =>', apiResponse);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response;
    }
  }
);

export const projectsAdapter = createEntityAdapter({
  // Keep projects sorted by creation date
  sortComparer: (prev, next) => {
    return new Date(next.created_at) - new Date(prev.created_at);
  },
});

const initialState = projectsAdapter.getInitialState({
  loading: false,
  error: null,
  groupBy: '',
  display: {
    entities: {
      groupings: {
        displayText: 'Groupings',
        groupTitle: true,
      },
      region: {
        displayText: 'Region',
        sortBy: undefined,
        enabled: false,
        width: 3,
        minWidth: '100px',
      },
      division: {
        displayText: 'Division',
        sortBy: undefined,
        enabled: false,
        width: 3,
        minWidth: '100px',
      },
      office: {
        displayText: 'Office',
        sortBy: undefined,
        enabled: false,
        width: 3,
        minWidth: '100px',
      },
      dates: {
        displayText: 'Dates',
        groupTitle: true,
      },
      start_date: {
        displayText: 'Start Date',
        sortBy: undefined,
        enabled: false,
        width: 3,
        minWidth: '100px',
      },
      sub_completion_date: {
        displayText: 'Scheduled Completion Date',
        sortBy: undefined,
        enabled: false,
        width: 3,
        minWidth: '100px',
      },
      est_completion_date: {
        displayText: 'Forecasted Completion Date',
        sortBy: undefined,
        enabled: true,
        width: 3,
        minWidth: '100px',
      },
      kpis: {
        displayText: 'KPIs',
        groupTitle: true,
      },
      pace: { displayText: 'Pace', sortBy: undefined, enabled: true, width: 1, minWidth: '100px' },
      pr: { displayText: 'PR', sortBy: undefined, enabled: true, width: 1, minWidth: '100px' },
      stage: {
        displayText: 'Stage',
        sortBy: undefined,
        enabled: false,
        width: 1,
        minWidth: '100px',
      },
      percent_complete_production: {
        displayText: '% Complete By Production',
        sortBy: undefined,
        enabled: false,
        width: 1,
        minWidth: '100px',
      },
      percent_complete_date: {
        displayText: '% Complete by Production',
        sortBy: undefined,
        enabled: false,
        width: 1,
        minWidth: '100px',
      },
      budget_divider: {
        displayText: 'Budget',
        groupTitle: true,
      },
      budgeted_cost: {
        displayText: 'Budgeted Cost',
        sortBy: undefined,
        enabled: false,
        width: 3,
        minWidth: '100px',
      },
      planned_cost: {
        displayText: 'Planned Cost',
        sortBy: undefined,
        enabled: false,
        width: 3,
        minWidth: '100px',
      },
      forecasted_cost: {
        displayText: 'Forecasted Cost',
        sortBy: undefined,
        enabled: false,
        width: 3,
        minWidth: '100px',
      },
    },
    keys: [
      'region',
      'division',
      'office',
      'start_date',
      'sub_completion_date',
      'est_completion_date',
      'pace',
      'pr',
      'stage',
      'percent_complete_production',
      'percent_complete_date',
      'budgeted_cost',
      'planned_cost',
      'forecasted_cost',
    ],
  },
  filters: {},
  customViews: {
    list: [{ id: 1, name: 'Custom View 1', parameters: { groupBy: '', display: [] } }],
    selected: 'Default',
  },
});

export const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearProjects: projectsAdapter.removeAll,
    addRemoveChartToDisplay: (state, action) => {
      let chartShape = action.payload.chartShape;
      let chartType = action.payload.chartType;
      let projectId = action.payload.projectId;
      if (state.entities[projectId].chartsToDisplay[chartShape].includes(chartType)) {
        state.entities[projectId].chartsToDisplay[chartShape] = state.entities[
          projectId
        ].chartsToDisplay[chartShape].filter((chart) => chart !== chartType);
      } else {
        state.entities[projectId].chartsToDisplay[chartShape].push(chartType);
      }
    },
    toggleDisplayOption: (state, action) => {
      const { key } = action.payload;
      state.display.entities[key].enabled = !state.display.entities[key].enabled;
      state.customViews.selected = `Custom`;
    },
    setProjectGroupBy: (state, action) => {
      state.groupBy = action.payload;
      state.customViews.selected = `Custom`;
    },
    resetToDefaultFilters: (state) => {
      state.groupBy = '';
      state.display = initialState.display;
      state.customViews.selected = `Default`;
    },
    setProjectsSortDirection: (state, action) => {
      const { key, direction } = action.payload;
      const currentDirection = state.display.entities[key].sortBy;
      state.display.entities[key].sortBy = currentDirection === direction ? undefined : direction;
      state.customViews.selected = `Custom`;
    },
    setCustomView: (state, action) => {
      const parameters = action.payload.parameters;
      state.groupBy = parameters.groupBy ? parameters.groupBy : '';
      state.customViews.selected = action.payload.name;
      const disabledKeys = difference(state.display.keys, parameters.display);
      if (disabledKeys) {
        disabledKeys.forEach((key) => (state.display.entities[key].enabled = false));
      }
      parameters.display.forEach((key) => (state.display.entities[key].enabled = true));
    },
  },
  extraReducers: (builder) => {
    // Create project reducers
    builder.addCase(saveProject.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      projectsAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(saveProject.pending, (state, action) => {
      state.loading = true;
      const projectId = action?.meta?.arg?.id;
      if (state.entities[projectId]) {
        state.entities[projectId].loading = true;
      }
    });
    builder.addCase(saveProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action?.payload?.errors;
      const projectId = action?.meta?.arg?.id;
      if (state.entities[projectId]) {
        state.entities[projectId].loading = false;
      }
    });

    // Update project reducers
    builder.addCase(updateProject.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      projectsAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(updateProject.pending, (state, action) => {
      const projectId = action.meta.arg.id;
      if (state.entities[projectId]) {
        state.entities[projectId].loading = true;
      }
    });
    builder.addCase(updateProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action?.payload?.errors;
      const projectId = action.meta.arg.id;
      if (state.entities[projectId]) {
        state.entities[projectId].loading = false;
      }
    });

    // Fetch projects reducers
    builder.addCase(fetchProjects.fulfilled, (state, action) => {
      projectsAdapter.setAll(state, action.payload);
      state.displayProjects = action.payload.map(({ id }) => id);
      state.loading = false;
      state.error = null;
    });
    builder.addCase(fetchProjects.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchProjects.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload
        ? action.payload
        : action.error
          ? action.error
          : 'Error occurred fetching projects';
    });

    // Fetch project reducers
    builder.addCase(fetchProject.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      projectsAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(fetchProject.pending, (state, action) => {
      const projectId = action.meta.arg.id;
      if (state.entities[projectId]) {
        state.entities[projectId].loading = true;
      } else {
        state.loading = true;
      }
    });
    builder.addCase(fetchProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      const projectId = action.meta.arg.id;
      if (state.entities[projectId]) {
        state.entities[projectId].loading = false;
      }
    });

    // Delete project reducers
    builder.addCase(deleteProject.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      projectsAdapter.removeOne(state, action.payload.id);
    });
    builder.addCase(deleteProject.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Duplicate project reducers
    builder.addCase(duplicateProject.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      projectsAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(duplicateProject.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(duplicateProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get project users reducers
    builder.addCase(fetchProjectUsers.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      const userDataObj = {};
      action.payload.users.forEach((user) => {
        userDataObj[user.id] = user['user_access_level'];
      });
      projectsAdapter.upsertOne(state, {
        id: parseInt(action.meta.arg),
        users: {
          userIds: action.payload.users.map((user) => user.id),
          userData: userDataObj,
        },
      });
    });
    builder.addCase(fetchProjectUsers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchProjectUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get project users reducers
    builder.addCase(fetchProjectUserPermissions.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(fetchProjectUserPermissions.fulfilled, (state, action) => {
      const { meta, payload } = action;
      const userDataObj = Object.fromEntries(
        payload.map((permission) => [permission.user_id, permission])
      );
      const userIds = payload.map((permission) => permission.user_id);

      state.loading = false;
      state.error = null;
      state.entities[meta.arg].user_permissions = {
        userIds,
        userData: userDataObj,
      };
    });

    builder.addCase(fetchProjectUserPermissions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(addProjectPermissions.pending, (state, action) => {
      const { projectId, permissions } = action.meta.arg;
      const userDataObj = Object.fromEntries(
        permissions.map((permission) => [permission.user_id, permission])
      );
      const userIds = permissions.map((permission) => permission.user_id);

      state.entities[projectId].user_permissions.userIds.push(...userIds);
      Object.assign(state.entities[projectId].user_permissions.userData, userDataObj);
    });

    builder.addCase(addProjectPermissions.fulfilled, (state, action) => {
      const { meta, payload } = action;
      const userDataObj = Object.fromEntries(
        payload.map((permission) => [permission.user_id, permission])
      );
      const userIds = payload.map((permission) => permission.user_id);

      state.loading = false;
      state.error = null;
      state.entities[meta.arg.projectId].user_permissions = {
        userIds,
        userData: userDataObj,
      };
    });

    builder.addCase(updateProjectPermissions.pending, (state, action) => {
      const { projectId, permissions } = action.meta.arg;
      permissions.forEach((permission) => {
        state.entities[projectId].user_permissions.userData[permission.user_id] = {
          ...state.entities[projectId].user_permissions.userData[permission.user_id],
          ...permission,
        };
      });
    });

    builder.addCase(updateProjectPermissions.fulfilled, (state, action) => {
      const { meta, payload } = action;
      const userDataObj = Object.fromEntries(
        payload.map((permission) => [permission.user_id, permission])
      );
      const userIds = payload.map((permission) => permission.user_id);

      state.loading = false;
      state.error = null;
      state.entities[meta.arg.projectId].user_permissions = {
        userIds,
        userData: userDataObj,
      };
    });

    builder.addCase(deleteProjectPermissions.pending, (state, action) => {
      const { projectId, userIds } = action.meta.arg;

      userIds.forEach((userId) => {
        delete state.entities[projectId].user_permissions.userData[userId];
      });

      state.entities[projectId].user_permissions.userIds = state.entities[
        projectId
      ].user_permissions.userIds.filter((id) => !userIds.includes(id));
    });

    builder.addCase(deleteProjectPermissions.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });

    builder.addCase(deleteProjectPermissions.rejected, (state, action) => {
      state.error = action.payload
        ? action.payload
        : action.error
          ? action.error
          : 'Error deleting user from project';
    });

    // PROJECT CUSTOM VIEWS
    builder.addCase(fetchProjectCustomViews.fulfilled, (state, action) => {
      state.customViews.list = action?.payload?.data;
    });

    builder.addCase(saveProjectCustomView.fulfilled, (state, action) => {
      if (action?.meta?.arg?.id) {
        const index = state.customViews.list.findIndex(({ id }) => id === action.meta.arg.id);
        state.customViews.list[index] = action.payload.data;
      } else {
        state.customViews.list.push(action.payload.data);
      }
    });

    builder.addCase(deleteProjectCustomView.fulfilled, (state, action) => {
      const index = state.customViews.list.findIndex(
        (customView) => customView.id === action.meta.arg
      );
      state.customViews.list.splice(index, 1);
    });

    builder.addCase(duplicateProjectCustomView.fulfilled, (state, action) => {
      state.customViews.list.push(action.payload.data);
    });

    builder.addMatcher(workspaceAPI.endpoints.setWorkspace.matchFulfilled, (state, action) => {
      projectsAdapter.removeAll(state);
    });
  },
});

const projectsReducer = projectsSlice.reducer;
export default projectsReducer;

export const {
  clearProjects,
  addRemoveChartToDisplay,
  toggleDisplayOption,
  setProjectGroupBy,
  setProjectsSortDirection,
  setCustomView,
  resetToDefaultFilters,
  searchProjects,
} = projectsSlice.actions;

/** Selectors **/

export const {
  selectById: selectProjectById,
  selectIds: selectProjectIds,
  selectEntities: selectProjectEntities,
  selectAll: selectAllProjects,
  selectTotal: selectTotalProjects,
} = projectsAdapter.getSelectors((state) => state.projects);

export const selectFilteredProjectIds = createSelector(
  [
    (state) => state.projects.filters,
    (state) => selectProjectIds(state),
    (state) => selectProjectEntities(state),
  ],
  (filters, projectIds, projectEntities) => {
    if (Object.keys(filters).length > 0) {
      Object.entries(filters).forEach(([filterName, filterValue]) => {
        projectIds.filter((projectId) => projectEntities[projectId][filterName] === filterValue);
      });
    } else {
      return projectIds;
    }
  }
);

export const selectFilteredSortedProjectIds = createSelector(
  [
    (state) => selectFilteredProjectIds(state),
    (state) => selectProjectEntities(state),
    (state) => state.projects.display.entities,
    (state) => state.projects.display.keys,
  ],
  (filteredProjectIds, projectEntities, displayEntities, displayKeys) => {
    const columns = [];
    const directions = [];
    displayKeys.forEach((optionKey) => {
      const displayEntity = displayEntities[optionKey];
      if (displayEntity.sortBy !== undefined) {
        columns.push(optionKey);
        directions.push(displayEntity.sortBy);
      }
    });
    if (columns.length !== 0) {
      return orderBy(projectEntities, columns, directions).map((entity) => entity.id);
    } else {
      return filteredProjectIds;
    }
  }
);

export const selectProjectIdsByGroupFilter = createSelector(
  [
    (state) => selectFilteredSortedProjectIds(state),
    (state) => state.projects.groupBy,
    (state) => selectProjectEntities(state),
  ],
  (projectIds, groupByField, projectEntities) => {
    if (projectIds.length > 0) {
      const output = {};
      const grouped = groupBy(
        projectIds.map((projectId) => projectEntities[projectId]),
        groupByField
      );
      let unGrouped = [];
      Object.entries(grouped).forEach(([groupName, projectEntities]) => {
        if (groupName === 'undefined' || groupName === 'null') {
          unGrouped = projectEntities.map((projectEntities) => projectEntities.id);
        } else {
          output[groupName] = projectEntities.map((projectEntities) => projectEntities.id);
        }
      });
      output['undefined'] = unGrouped;
      return output;
    } else {
      return undefined;
    }
  }
);
